import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import Cropper from "react-easy-crop";
import { GetCroppedImg } from "../../../utils/GetCroppedImg";

interface CropperModalProps {
  open: boolean;
  onClose: () => void;
  level: number;
  data?: { imageSrc: string; extension: string };
  setResults: (file: File) => void;
}

const CropperModal = ({
  open,
  onClose,
  level,
  data = { imageSrc: "", extension: "png" },
  setResults,
}: CropperModalProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCropComplete = (_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const generateRandomFileName = (extension: string) => {
    const randomString = Math.random().toString(36).substring(2, 10);
    const timestamp = Date.now();
    return `${timestamp}_${randomString}.${extension}`;
  };

  const handleCropSubmit = async () => {
    if (!croppedAreaPixels || !data.imageSrc) return;
    setIsLoading(true);

    try {
      const croppedBlob = await GetCroppedImg(data.imageSrc, croppedAreaPixels);
      const fileName = generateRandomFileName(data.extension);
      const file = new File([croppedBlob], fileName, { type: croppedBlob.type });
      setResults(file);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
    setIsLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="popup_service"
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        borderRadius: "18px",
        "& .MuiDialog-paper": {
          borderRadius: "18px",
          maxWidth: "500px",
        },
      }}
    >
      <DialogContent>
        {data.imageSrc && data.imageSrc.trim() !== "" ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "400px",
              overflow: "hidden",
              borderRadius: "9px",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Cropper
              image={data.imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          </Box>
        ) : (
          <Typography color="error" textAlign="center">
            No Image Selected.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "center",
          gap: 2,
          paddingBottom: 3,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            border: "1px solid #F475A4",
            borderRadius: "8px",
            background: "#fff",
            fontSize: "16px",
            fontFamily: "Montserrat",
            color: "#F475A4",
            textTransform: "capitalize",
            padding: "7px 28px",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCropSubmit}
          disabled={isLoading || !data.imageSrc}
          sx={{
            border: "1px solid #F475A4",
            borderRadius: "8px",
            background: "#F475A4",
            fontSize: "16px",
            fontFamily: "Montserrat",
            color: "#fff",
            textTransform: "capitalize",
            padding: "7px 28px",
            "&:hover": {
              backgroundColor: "#e0669c",
            },
            "&:disabled": {
              backgroundColor: "#f5f5f5",
              color: "#ccc",
              border: "1px solid #ccc",
            },
          }}
          autoFocus
        >
          {isLoading ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
              Cropping...
            </Box>
          ) : (
            "Crop & Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CropperModal;